import { Component, Emit, Inject, Prop, Vue } from 'vue-property-decorator'
import { Nullable, StrictNullable } from '@/shared/types/app.types'
import {
    InvoiceReceiptPatchRequest,
    InvoiceReceiptResponse,
    InvoiceReceiptStatus
} from '@/api/services/billing/invoice-receipt/billing-invoice-receipt.dto'
import { billingInvoiceReceiptService } from '@/api/services/billing/invoice-receipt/billing-invoice-receipt.service'
import { InvoiceResponse } from '@/api/services/billing/common-invoice/billing-common-invoice.dto'
import { AppliedBillableConceptResponse } from '@/api/services/billing/applied-billable-concept/billing-applied-billable-concept.dto'
import { Validator } from 'vee-validate'
import { PromiseUtils } from '@/shared/util/promise-utils'
import { ToastUtils } from '@/shared/util/toast-utils'
import { store } from '@/store/store'
import { DateUtils } from '@/shared/util/date-utils'
import { BillingOperationWithConceptsGuard } from '@/modules/billing-module/shared/guards/billing-operation-with-concepts-guard'

@Component
export default class BillingModalInvoiceReceiptActionsModal extends Vue {
    @Prop({ type: Object }) readonly invoiceReceipt!: Nullable<InvoiceReceiptResponse>
    @Prop({ type: Object }) readonly invoice!: Nullable<InvoiceResponse>
    @Prop({ type: Boolean, default: true }) readonly selectionMode!: boolean
    @Prop({ type: Number, default: 0 }) readonly invoiceAppliedBillableConceptsCount!: number
    @Prop({ type: Array, default: () => [] }) readonly appliedBillableConcepts!: AppliedBillableConceptResponse[]

    @Inject() externalValidator!: Validator

    constructor() {
        super()
        this.$validator = this.externalValidator
    }

    @Emit()
    refresh(invoiceReceiptId: StrictNullable<number>): StrictNullable<number> {
        return invoiceReceiptId
    }

    private get _invoiceReceiptGenerated(): boolean {
        return this.invoiceReceipt?.status.code === InvoiceReceiptStatus.GENERATED
    }
    private get _invoiceReceiptVerified(): boolean {
        return this.invoiceReceipt?.status.code === InvoiceReceiptStatus.VERIFIED
    }

    private get appliedBillableConceptsIds(): number[] {
        return this.appliedBillableConcepts.map(abc => abc.id)
    }

    get visibleAdd(): boolean {
        return this.selectionMode &&
            (!this.invoiceReceipt || this._invoiceReceiptGenerated)
    }
    get canAddSelected(): boolean {
        return this.appliedBillableConceptsIds.length > 0
    }

    get visibleUpdateInvoiceReceipt(): boolean {
        return !this.selectionMode &&
            (this._invoiceReceiptVerified || this._invoiceReceiptGenerated)
    }

    get visibleDeleteInvoiceReceipt(): boolean {
        return !this.selectionMode &&
            this._invoiceReceiptGenerated
    }
    get visibleVerifyInvoiceReceipt(): boolean {
        return !this.selectionMode &&
            this._invoiceReceiptGenerated &&
            this.invoiceAppliedBillableConceptsCount > 0
    }

    private async _createInvoiceReceiptFromInvoice(): Promise<void> {
        if (this.invoice) {
            const response =
                await PromiseUtils.toCallSecure(
                    billingInvoiceReceiptService.createFromInvoice(
                        store.state.labId,
                        this.invoice.id))
            if (response !== null) {
                ToastUtils.showToastSuccess(this.$tc('accounting.invoiceReceipt.generated'))
                this.refresh(response.id) // emits an event
            }
        }
    }
    private async _createInvoiceReceiptFromAppliedBillableConcepts(): Promise<void> {
        if (this.appliedBillableConceptsIds.length) {
            const response =
                await PromiseUtils.toCallSecure(
                    billingInvoiceReceiptService.createFromAppliedBillableConcepts(
                        store.state.labId,
                        this.appliedBillableConceptsIds))
            if (response !== null) {
                ToastUtils.showToastSuccess(this.$tc('accounting.invoiceReceipt.generated'))
                this.refresh(response.id) // emits an event
            }
        }
    }

    private async _addAllSelectedAppliedBillableConceptsToInvoiceReceipt(): Promise<void> {
        if (this.invoiceReceipt) {
            const response =
                await PromiseUtils.toCallSecure(
                    billingInvoiceReceiptService.addAllConcepts(
                        store.state.labId,
                        this.invoiceReceipt.id))
            if (response !== null) {
                ToastUtils.showToastSuccess(this.$tc('accounting.concept.added'))
                this.refresh(this.invoiceReceipt.id) // emits an event
            }
        }
    }
    private async _addSelectedAppliedBillableConceptsToInvoiceReceipt(): Promise<void> {
        if (this.invoiceReceipt && this.appliedBillableConceptsIds.length) {
            const response =
                await PromiseUtils.toCallSecure(
                    billingInvoiceReceiptService.addConcepts(
                        store.state.labId,
                        this.invoiceReceipt.id,
                        this.appliedBillableConceptsIds))
            if (response !== null) {
                ToastUtils.showToastSuccess(this.$tc('accounting.concept.added'))
                this.refresh(this.invoiceReceipt.id) // emits an event
            }
        }
    }

    async onAddAll(): Promise<void> {
        if (this.invoiceReceipt) {
            const { invoice, appliedBillableConceptCount } = this.invoiceReceipt
            const numberOfConcepts = invoice.appliedBillableConceptsCount - appliedBillableConceptCount
            await BillingOperationWithConceptsGuard.run(
                numberOfConcepts,
                this._addAllSelectedAppliedBillableConceptsToInvoiceReceipt
            )
        } else if (this.invoice) {
            await BillingOperationWithConceptsGuard.run(
                this.invoice.appliedBillableConceptsCount,
                this._createInvoiceReceiptFromInvoice
            )
        }
    }
    async onAddSelected(): Promise<void> {
        if (this.invoiceReceipt) {
            await BillingOperationWithConceptsGuard.run(
                this.appliedBillableConceptsIds.length,
                this._addSelectedAppliedBillableConceptsToInvoiceReceipt
            )
        } else {
            await BillingOperationWithConceptsGuard.run(
                this.appliedBillableConceptsIds.length,
                this._createInvoiceReceiptFromAppliedBillableConcepts
            )
        }
    }

    private async _updateInvoiceReceipt(conceptsRealTotalValue?: StrictNullable<number>): Promise<boolean> {
        const valid = await this.$validator.validateAll()
        if (valid && this.invoiceReceipt) {
            const { id, externalReference, paymentDate, paymentMethod, realTotalValue } = this.invoiceReceipt
            const request: InvoiceReceiptPatchRequest = this._invoiceReceiptVerified ? {
                id,
                externalReference,
                paymentDate: paymentDate ? DateUtils.formatDate(new Date(paymentDate), DateUtils.datePattern) : undefined,
                paymentMethodId: paymentMethod?.id
            } : {
                id,
                realTotalValue: conceptsRealTotalValue ?? realTotalValue
            }
            const response =
                await PromiseUtils.toCallSecure(
                    billingInvoiceReceiptService.patch(store.state.labId, request))
            return response !== null
        }
        return false
    }
    async onUpdateInvoiceReceipt(): Promise<void> {
        if (this.invoiceReceipt && await this._updateInvoiceReceipt()) {
            ToastUtils.showToastSuccess(this.$tc('billing.modal.invoiceReceipt.message.updated'))
            this.refresh(this.invoiceReceipt.id) // emits an event
        }
    }

    async onDeleteInvoiceReceipt(): Promise<void> {
        if (this.invoiceReceipt) {
            const { id, appliedBillableConceptCount } = this.invoiceReceipt
            await BillingOperationWithConceptsGuard.run(
                appliedBillableConceptCount,
                async () => {
                    const response =
                        await PromiseUtils.toCallSecure(
                            billingInvoiceReceiptService.delete(store.state.labId, id))
                    if (response !== null) {
                        ToastUtils.showToastSuccess(this.$tc('billing.modal.invoiceReceipt.message.deleted'))
                        this.refresh(null) // emits an event
                    }
                }
            )
        }
    }
    async onVerifyInvoiceReceipt(): Promise<void> {
        if (this.invoiceReceipt) {
            const { id, realTotalValue, appliedBillableConceptCount } = this.invoiceReceipt
            const value = realTotalValue ?? this.invoiceReceipt.conceptsRealTotalValue
            await BillingOperationWithConceptsGuard.run(
                appliedBillableConceptCount,
                async () => {
                    if (!this.visibleUpdateInvoiceReceipt || await this._updateInvoiceReceipt(value)) {
                        const response =
                            await PromiseUtils.toCallSecure(
                                billingInvoiceReceiptService.verify(store.state.labId, id))
                        if (response !== null) {
                            ToastUtils.showToastSuccess(this.$tc('billing.modal.invoiceReceipt.message.verified'))
                            this.refresh(id) // emits an event
                        }
                    }
                }
            )
        }
    }
}
